import { useState } from "react";
import JobLandingPage from "../components/jobs/JobLandingPage";
import ZoomableScheduleCanvas from "../../../components/availability/ZoomableScheduleCanvas";
import ScheduleSummaryPage from "../components/jobs/ScheduleSummaryPage";
import IDScanPage from "../components/jobs/IDScanPage";
import IDVerificationPage from "../components/jobs/IDVerificationPage";
import InterviewTextQuestion from "../components/jobs/InterviewTextQuestion";
import InterviewMultipleChoiceQuestion from "../components/jobs/InterviewMultipleChoiceQuestion";
import InterviewVideoQuestion from "../components/jobs/InterviewVideoQuestion";
import { ApplicationSuccessPage } from "../components/jobs/ApplicationSuccessPage";
import type { IDVerificationData } from "../components/jobs/IDVerificationPage";
import type { WeeklyAvailability } from "@applicant-reviewer/api/availability";
import { DAYS_OF_WEEK, SLOT_STATUS, SLOTS_PER_DAY } from "@applicant-reviewer/api/availability";
import { useJobPosting } from "../../../hooks/queries/useJobPostings";

type Screen = "landing" | "schedule" | "summary" | "idscan" | "idverify" | "interview" | "success";

function getJobId(): string {
  const params = new URLSearchParams(window.location.search);
  const queryJobId = params.get("jobId");
  if (queryJobId) return queryJobId;
  // Try path-based: /apply/:jobId
  const pathMatch = window.location.pathname.match(/\/apply\/([^/]+)/);
  if (pathMatch) return pathMatch[1];
  return "";
}

export default function JobLandingDemo() {
  const [currentScreen, setCurrentScreen] = useState<Screen>("landing");
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  const jobId = getJobId();
  const { data: job, isLoading, isError } = useJobPosting(jobId);

  const sortedQuestions = job
    ? [...job.questions].sort((a, b) => {
        const aIsVideo = a.type === "video";
        const bIsVideo = b.type === "video";
        if (aIsVideo && !bIsVideo) return 1;
        if (!aIsVideo && bIsVideo) return -1;
        return 0;
      })
    : [];

  const JOB_DATA = job
    ? {
        companyName: job.companyName,
        companyLogo: job.companyLogo ?? "",
        location: job.locationName ?? "",
      }
    : { companyName: "", companyLogo: "", location: "" };

  const [availability, setAvailability] = useState<WeeklyAvailability>(() =>
    DAYS_OF_WEEK.map((day) => ({
      dayOfWeek: day,
      slots: Array.from({ length: SLOTS_PER_DAY }, (_, index) => ({
        index,
        status: SLOT_STATUS.UNAVAILABLE,
      })),
    }))
  );

  const handleWatchVideo = () => alert("Video player would open here!");

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-white">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-[#0156FF] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[17px] text-[#8e8e8f]">Loading job details...</p>
        </div>
      </div>
    );
  }

  if (isError || !job) {
    return (
      <div className="flex items-center justify-center h-screen bg-white">
        <div className="text-center px-6">
          <p className="text-[19px] font-bold text-[#28292d] mb-2">Job Not Found</p>
          <p className="text-[17px] text-[#8e8e8f]">
            This job posting is no longer available or the link may be invalid.
          </p>
        </div>
      </div>
    );
  }

  // Interview Questions
  if (currentScreen === "interview") {
    const currentQuestion = sortedQuestions[currentQuestionIndex];
    const totalQuestions = sortedQuestions.length;

    const handleQuestionBack = () => {
      if (currentQuestionIndex > 0) {
        setCurrentQuestionIndex(currentQuestionIndex - 1);
      } else {
        setCurrentScreen("idverify");
      }
    };

    const handleQuestionNext = (_answer: unknown) => {
      if (currentQuestionIndex < totalQuestions - 1) {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
      } else {
        setCurrentScreen("success");
      }
    };

    if (currentQuestion.type === "text") {
      return (
        <InterviewTextQuestion
          key={currentQuestionIndex}
          {...JOB_DATA}
          currentStep={currentQuestionIndex + 1}
          totalSteps={totalQuestions}
          question={currentQuestion.prompt}
          onBack={handleQuestionBack}
          onNext={handleQuestionNext}
          onWatchVideo={handleWatchVideo}
        />
      );
    }

    if (currentQuestion.type === "multiple_choice") {
      return (
        <InterviewMultipleChoiceQuestion
          key={currentQuestionIndex}
          {...JOB_DATA}
          currentStep={currentQuestionIndex + 1}
          totalSteps={totalQuestions}
          question={currentQuestion.prompt}
          options={currentQuestion.options || []}
          onBack={handleQuestionBack}
          onNext={handleQuestionNext}
          onWatchVideo={handleWatchVideo}
        />
      );
    }

    if (currentQuestion.type === "video") {
      return (
        <InterviewVideoQuestion
          key={currentQuestionIndex}
          {...JOB_DATA}
          currentStep={currentQuestionIndex + 1}
          totalSteps={totalQuestions}
          question={currentQuestion.prompt}
          maxDuration={currentQuestion.maxDuration}
          onBack={handleQuestionBack}
          onNext={handleQuestionNext}
          onWatchVideo={handleWatchVideo}
        />
      );
    }

    if (currentQuestion.type === "yes_no") {
      return (
        <InterviewMultipleChoiceQuestion
          key={currentQuestionIndex}
          {...JOB_DATA}
          currentStep={currentQuestionIndex + 1}
          totalSteps={totalQuestions}
          question={currentQuestion.prompt}
          options={["Yes", "No"]}
          onBack={handleQuestionBack}
          onNext={handleQuestionNext}
          onWatchVideo={handleWatchVideo}
        />
      );
    }

    // Fallback
    return (
      <InterviewTextQuestion
        key={currentQuestionIndex}
        {...JOB_DATA}
        currentStep={currentQuestionIndex + 1}
        totalSteps={totalQuestions}
        question={currentQuestion.prompt}
        onBack={handleQuestionBack}
        onNext={handleQuestionNext}
        onWatchVideo={handleWatchVideo}
      />
    );
  }

  if (currentScreen === "success") {
    return (
      <ApplicationSuccessPage
        companyName={JOB_DATA.companyName}
        companyLogo={JOB_DATA.companyLogo}
        location={JOB_DATA.location}
        onWatchVideo={handleWatchVideo}
      />
    );
  }

  if (currentScreen === "idverify") {
    return (
      <IDVerificationPage
        companyName={JOB_DATA.companyName}
        companyLogo={JOB_DATA.companyLogo}
        location={JOB_DATA.location}
        onWatchVideo={handleWatchVideo}
        onBack={() => setCurrentScreen("idscan")}
        onConfirm={(_data: IDVerificationData) => {
          setCurrentQuestionIndex(0);
          setCurrentScreen("interview");
        }}
      />
    );
  }

  if (currentScreen === "idscan") {
    return (
      <IDScanPage
        companyName={JOB_DATA.companyName}
        companyLogo={JOB_DATA.companyLogo}
        location={JOB_DATA.location}
        onWatchVideo={handleWatchVideo}
        onCapture={(_photoDataUrl: string) => setCurrentScreen("idverify")}
      />
    );
  }

  if (currentScreen === "summary") {
    return (
      <ScheduleSummaryPage
        availability={availability}
        hourlyRate={job.compensation?.min ?? 0}
        companyName={JOB_DATA.companyName}
        companyLogo={JOB_DATA.companyLogo}
        location={JOB_DATA.location}
        onWatchVideo={handleWatchVideo}
        onBack={() => setCurrentScreen("schedule")}
        onConfirm={() => setCurrentScreen("idscan")}
      />
    );
  }

  if (currentScreen === "schedule") {
    return (
      <ZoomableScheduleCanvas
        availability={availability}
        onChange={setAvailability}
        hourlyRate={job.compensation?.min ?? 0}
        onSave={() => setCurrentScreen("summary")}
        companyName={JOB_DATA.companyName}
        companyLogo={JOB_DATA.companyLogo}
        location={JOB_DATA.location}
        onWatchVideo={handleWatchVideo}
        onBack={() => setCurrentScreen("landing")}
      />
    );
  }

  // Landing screen
  return (
    <JobLandingPage
      job={job}
      onNext={(_weeklyPay: number, _daysPerWeek: number, _hoursPerDay: number) =>
        setCurrentScreen("schedule")
      }
      onWatchVideo={handleWatchVideo}
    />
  );
}
